import { pageConstants } from '@helper/constants';
import { normalizeQrCartMemo } from './orderMemo';

const cleanToken = value => String(value || '').trim();
const activeVisitKey = 'orderpro_qr_active_visit';
export const isQrVisit = value => /^visit-[a-f0-9]{32}$/.test(cleanToken(value));

export const qrVisitFromFragment = hash => {
    const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
    if (!params.has('visit')) return null;
    const id = params.get('visit');
    return /^[a-f0-9]{32}$/.test(id || '') ? `visit-${id}` : '';
};

export const consumeQrVisit = (hash = window.location.hash) => {
    const visit = qrVisitFromFragment(hash);
    if (visit === null) return storedQrToken();
    if (visit) sessionStorage.setItem(activeVisitKey, visit);
    else sessionStorage.removeItem(activeVisitKey);
    const params = new URLSearchParams(String(hash).replace(/^#/, ''));
    params.delete('visit');
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${params.toString() ? `#${params}` : ''}`);
    return visit;
};

export const getQrCsrf = visit => isQrVisit(visit)
    ? sessionStorage.getItem(`orderpro_qr_csrf:${visit}`) || '' : '';

export const setQrBrowserSession = (visit, response) => {
    if (!isQrVisit(visit) || response?.visit !== visit || response?.msg !== 'ok'
        || !/^[a-f0-9]{64}$/.test(response?.csrf || '')
        || !/^browser-[a-f0-9]{64}$/.test(response?.token || '')) throw new Error('invalid_qr_session');
    sessionStorage.setItem(`orderpro_qr_csrf:${visit}`, response.csrf);
    const previous = getQrSession(visit);
    const metadata = previous?.token === response.token ? { ...previous } : {};
    ['token', 'app_no', 's_no', 'area_no', 'table_no', 'target_kind'].forEach(key => { metadata[key] = response[key]; });
    setQrSession(visit, metadata);
};

const readJson = key => {
    try {
        return JSON.parse(localStorage.getItem(key) || 'null');
    } catch (_) {
        return null;
    }
};

const shopScope = session => session?.target_kind === 'shop' && Number(session.app_no)>0 && Number(session.s_no)>0
    ? `${Number(session.app_no)}:${Number(session.s_no)}` : null;
const scopedKey = (name, tokenqr) => {
    const token=cleanToken(tokenqr);
    const session=name==='cart'?readJson(`orderpro_qr_session:${token}`):null;
    const scope=!isQrVisit(token)&&shopScope(session);
    return `orderpro_qr_${name}:${scope?'shop:'+scope:session?.token?'visit:'+session.token:token}`;
};

export const getQrBrowserSessions = () => readJson('orderpro_qr_browser_sessions') || {};

// A public source URL is not a checkout identity. Keep pending attempts stable
// across encrypted entry URLs. The signed visit already separates shops,
// guests and (for table targets) assignment/table/opening/day boundaries.
export const getQrAttemptScope = tokenqr => {
    const session=getQrSession(tokenqr);
    return session?.token ? `visit:${Number(session.app_no)||0}:${Number(session.s_no)||0}:${session.token}` : cleanToken(tokenqr);
};

export const storedQrToken = () => {
    const visit = cleanToken(sessionStorage.getItem(activeVisitKey));
    return isQrVisit(visit) ? visit : '';
};

export const resolveQrToken = routeToken => cleanToken(routeToken) || storedQrToken();

export const rememberQrToken = tokenqr => {
    const token = cleanToken(tokenqr);
    if (isQrVisit(token)) sessionStorage.setItem(activeVisitKey, token);
    else if (token) localStorage.setItem('tokenqr', token);
    return token;
};

export const qrPath = (tokenqr, page = 'home', identifier = '') => {
    const token = cleanToken(tokenqr);
    const root = String(pageConstants?.subFolder || '').replace(/\/$/, '');
    if (!token) return `${root}/invalid_qr` || '/invalid_qr';

    const base = isQrVisit(token) ? `${root}/order` : `${root}/${encodeURIComponent(token)}`;
    const encodedId = encodeURIComponent(String(identifier || ''));
    const paths = {
        home: base,
        cart: `${base}/cart`,
        search: `${base}/search_food`,
        food: `${base}/food_detail/${encodedId}`,
        orders: `${base}/order_list`,
        banner: `${base}/banner_detail/${encodedId}`,
    };
    return paths[page] || base;
};

export const getQrSession = tokenqr => {
    const token = cleanToken(tokenqr);
    if (!token) return null;

    const scoped = readJson(scopedKey('session', token));
    if (scoped?.tokenqr === token) {
        const scope=!isQrVisit(token)&&shopScope(scoped);
        const shared=scope&&readJson(`orderpro_qr_shop_session:${scope}`);
        return shared ? {...shared,tokenqr:token}:scoped;
    }

    const legacy = readJson('user');
    if (legacy?.tokenqr === token) {
        localStorage.setItem(scopedKey('session', token), JSON.stringify(legacy));
        return legacy;
    }
    return null;
};

export const setQrSession = (tokenqr, session) => {
    const token = rememberQrToken(tokenqr);
    if (!token || !session) return;
    const previous=getQrSession(token);
    if(previous?.token && previous.token!==session.token) clearQrCart(token);
    const publicSession = { ...session };
    delete publicSession.csrf;
    const value = { ...publicSession, tokenqr: token };
    const scope=!isQrVisit(token)&&shopScope(value);
    if(scope){
        const shared=readJson(`orderpro_qr_shop_session:${scope}`);
        if(shared?.token && shared.token!==value.token){
            localStorage.removeItem(`orderpro_qr_cart:shop:${scope}`);
            if(readJson('cart')?.token===shared.token)localStorage.removeItem('cart');
        }
        localStorage.setItem(`orderpro_qr_shop_session:${scope}`,JSON.stringify(value));
        localStorage.setItem('orderpro_qr_browser_sessions',JSON.stringify({...getQrBrowserSessions(),[scope]:value.token}));
    }
    localStorage.setItem(scopedKey('session', token), JSON.stringify(value));
    // Keep the legacy key during the compatibility window.
    localStorage.setItem('user', JSON.stringify(value));
};

export const getQrCart = tokenqr => {
    const token = cleanToken(tokenqr);
    if (!token) return null;

    const scoped = readJson(scopedKey('cart', token));
    const session = getQrSession(token);
    if (scoped && session?.token && scoped.token===session.token) return normalizeQrCartMemo({...scoped,tokenqr:token});

    const legacy = readJson('cart');
    const belongsToSession = legacy && session?.token
        && String(legacy.token || '') === String(session.token);
    if (legacy && (legacy.tokenqr === token || belongsToSession)) {
        const migrated = normalizeQrCartMemo({ ...legacy, tokenqr: token });
        localStorage.setItem(scopedKey('cart', token), JSON.stringify(migrated));
        return migrated;
    }
    return null;
};

export const setQrCart = (tokenqr, cart) => {
    const token = cleanToken(tokenqr);
    if (!token || !cart) return;
    const value = { ...cart, tokenqr: token };
    localStorage.setItem(scopedKey('cart', token), JSON.stringify(value));
    // Keep the legacy key during the compatibility window.
    localStorage.setItem('cart', JSON.stringify(value));
};

export const clearQrCart = tokenqr => {
    const token = cleanToken(tokenqr);
    if (!token) return;
    localStorage.removeItem(scopedKey('cart', token));

    const legacy = readJson('cart');
    const session = getQrSession(token);
    if (legacy?.tokenqr === token || (session?.token && legacy?.token === session.token)) {
        localStorage.removeItem('cart');
    }
};
